import { customElement, property, state } from 'lit/decorators.js';
import { css, html, LitElement, nothing } from 'lit';
import './d-organization-edit-access.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-view-info.js';

import type { FeatureStates } from '../../store/selectors/featureStates';
import type { UserForAccess } from '../../library/editors/components/user-for-access';
import { headerViewStyles } from 'src/layout/parts/header-view-styles';
import { t } from 'src/i18n';
import { fetchOrganizationDevices } from 'src/services/organization-devices';
import type { OrganizationDevice } from 'src/services/organization-devices-types';

/**
 *
 */
@customElement('d-organization-users')
export class DOrganizationUsers extends LitElement {
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: block;
      }
    `,
  ];
  @property({ type: Array })
  users: UserForAccess[] = [];
  @property({ type: Object })
  currentUser!: UserForAccess;
  @property({ type: String })
  accountOwnerEmail!: string;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Number })
  organizationId = 0;
  @state()
  private devices: OrganizationDevice[] = [];
  private devicesFetchedForOrganizationId = 0;
  private get noContactsInfo() {
    return t('access.noContactsInfo');
  }

  private get currentUserIsAccountOwner(): boolean {
    return this.currentUser?.email !== '' && this.currentUser?.email === this.accountOwnerEmail;
  }

  private get employees(): UserForAccess[] {
    return this.users.filter((u) => {
      return u.partnerName === undefined || u.partnerName === '';
    });
  }

  private get contacts(): UserForAccess[] {
    return this.users.filter((u) => {
      return u.partnerName !== undefined && u.partnerName !== '';
    });
  }

  protected updated() {
    if (
      this.currentUserIsAccountOwner &&
      this.organizationId !== 0 &&
      this.devicesFetchedForOrganizationId !== this.organizationId
    ) {
      this.devicesFetchedForOrganizationId = this.organizationId;
      fetchOrganizationDevices(this.organizationId).then((devices) => {
        this.devices = devices;
      });
    }
  }

  private devicesForUser(user: UserForAccess): OrganizationDevice[] {
    return this.devices.filter((d) => d.employeeUuid === user.uuid || d.contactPersonUuid === user.uuid);
  }

  renderSection(header: string, usersForAccess: UserForAccess[]) {
    return html` <div class="settings-section">
      <h2>${header}</h2>
      <d-section outskirts vertical>
        ${usersForAccess.map(
          (item) => html`
            <d-organization-edit-access
              .users=${this.users}
              .editUser=${item}
              .currentUser=${this.currentUser}
              .ownerEmail=${this.accountOwnerEmail}
              .devices=${this.devicesForUser(item)}
            ></d-organization-edit-access>
          `,
        )}
        ${usersForAccess.length === 0 ? html` <d-view-info .content=${this.noContactsInfo}></d-view-info> ` : nothing}
      </d-section>
    </div>`;
  }

  render() {
    return html`
      ${this.renderSection(t('access.staffHeading'), this.employees)}
      ${this.featureStates.core ? html` ${this.renderSection(t('access.contactsHeading'), this.contacts)}` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-users': DOrganizationUsers;
  }
}
